import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEmbedAuthHandshake } from '../hooks/useEmbedAuthHandshake';
import {
  getMsr2Base,
  isTrustedMsr2Message,
  withEmbedAuthParams,
} from '../utils/embedAuth';
import { isTrustedResourceId } from '../utils/embedWorkflow';
import { AppLoader } from './Loader';

/**
 * The embed lifecycle as a single source of truth. Modelling it as one status
 * avoids the impossible combinations (e.g. ready while still showing the loader)
 * that separate `isLoading` flags allow.
 *
 * - `loading` — iframe is mounted but hidden, waiting for its `onLoad`
 * - `ready`   — the iframe has loaded; it is visible
 */
type EmbedStatus = 'loading' | 'ready';

interface EmbedViewProps {
  embedType: string;
  loadingMessage?: string;
  resourceId?: string;
}

/**
 * Generic iframe embed view for MyScreenRights embedded pages.
 * Renders an iframe pointing to `${MYSCREENRIGHTS_BASE}/embed/${embedType}/${resourceId}`,
 * with automatic height adjustment via postMessage.
 *
 * The URL also carries `?parentAuth=1`, which tells MSR2 this window will hand
 * it a Cognito session over postMessage (see utils/embedAuth.ts, APP-2221).
 *
 * @param embedType - The embed path segment (e.g. "potential-claim", "competing-claim", "title", "payments", "consolidated-payments", "royalties")
 * @param resourceId - Optional explicit resource id; falls back to the `:resourceId` route param
 * @param loadingMessage - Optional custom loading message
 */
export const EmbedView = ({
  embedType,
  loadingMessage = 'Loading, please stand by...',
  resourceId: resourceIdProp,
}: EmbedViewProps) => {
  const { resourceId: routeResourceId } = useParams<{ resourceId?: string }>();
  const resourceId = resourceIdProp ?? routeResourceId;
  // null until the embed reports its content height via postMessage.
  const [measuredHeight, setMeasuredHeight] = useState<null | number>(null);
  const [status, setStatus] = useState<EmbedStatus>('loading');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const base = getMsr2Base();

  // Every embed type needs a resourceId, so leave the URL undefined until it
  // arrives rather than pointing the iframe at an incomplete path. A route param
  // arrives with `%2F` decoded, so only a plain id may reach the path.
  const iframeSrc = isTrustedResourceId(resourceId)
    ? withEmbedAuthParams(`${base}/embed/${embedType}/${resourceId}`)
    : undefined;

  // MSR2 cannot reach its own Cognito session from a cross-site iframe, so it
  // asks this window for one. See utils/embedAuth.ts (APP-2221).
  useEmbedAuthHandshake(iframeRef, Boolean(iframeSrc));

  useEffect(() => {
    setStatus('loading');
    setMeasuredHeight(null);

    if (!iframeSrc) {
      return;
    }

    const handleMessage = (event: MessageEvent) => {
      if (!isTrustedMsr2Message(event, iframeRef.current) || !event.data) {
        return;
      }

      if (event.data.type === 'setHeight' && event.data.scrollHeight) {
        // Size the iframe to its content. `setHeight` may be late, blocked, or
        // never sent by an older embed, so it only refines the height — it is
        // not what reveals the iframe (that happens on load, below).
        setMeasuredHeight(event.data.scrollHeight);
      }

      if (event.data.type === 'scrollToTop') {
        // The embed emits this when navigating to the next page; scroll the
        // app's main viewport back to the top (matches the embed PoC behavior).
        document
          .querySelector('main')
          ?.scrollTo({ behavior: 'smooth', top: 0 });
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [iframeSrc]);

  // Reveal once the frame has loaded so a working embed is always shown, even
  // when its `setHeight` message is delayed or never arrives.
  const handleIframeLoad = () => {
    setStatus('ready');
  };

  return (
    <>
      <link href={base} rel="preconnect" />

      <div className="flex w-full flex-col rounded-xl border border-border bg-white p-5">
        {status === 'loading' && (
          <div className="flex min-h-96 flex-col items-center justify-center gap-4">
            <AppLoader.Spinner size="8" />
            <p className="text-sm text-muted-foreground">{loadingMessage}</p>
          </div>
        )}

        {iframeSrc && (status === 'loading' || status === 'ready') && (
          <iframe
            // Collapse and hide while loading so the unloaded frame never flashes
            // as a blank box; reveal at the measured height once it has loaded.
            className={`w-full overflow-hidden ${status === 'loading' ? 'invisible h-0 min-h-0' : 'min-h-96'}`}
            ref={iframeRef}
            scrolling="no"
            src={iframeSrc}
            style={{
              border: 'none',
              height:
                measuredHeight !== null ? `${measuredHeight}px` : undefined,
            }}
            onLoad={handleIframeLoad}
          />
        )}
      </div>
    </>
  );
};
